import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Eye, Download, FileText } from 'lucide-react';
import brochureImage from '@assets/images/brochure01.jpg';
import brochurePdf from '@assets/docs/brochure01.pdf';
import diskursuPdf from '@assets/docs/DISKURSU_V2.pdf';
import oratioPdf from '@assets/docs/ORATIO_SAPIENTIAE.pdf';
import fratelliTuttiPdf from '@assets/docs/Fratelli-Tutti.pdf';
import politikaFaramaseutikaPdf from '@assets/docs/Politika-Faramaseutika.pdf';
import legadoFidelPdf from '@assets/docs/Legado-Fidel.pdf';
import saudeDigitalPdf from '@assets/docs/Saude-Digital.pdf';
import guardaNainPdf from '@assets/docs/Guarda-nain-Foho-Sagradu.pdf';

const brochures = [
  {
    title: 'Brochure',
    pdf: brochurePdf,
    image: brochureImage,
    downloadName: 'brochure01.pdf',
    hasImage: true,
  },
  {
    title: 'DISKURSU AKADÉMIKU-5 de Setembru de 2026-V2',
    subtitle: '5 de Setembru de 2026',
    pdf: diskursuPdf,
    downloadName: 'DISKURSU_V2.pdf',
    hasImage: false,
  },
  {
    title: 'ORATIO SAPIENTIAE-Unital-17 Agostu 2026-versaun proferida',
    subtitle: 'Unital — 17 Agostu 2026',
    pdf: oratioPdf,
    downloadName: 'ORATIO_SAPIENTIAE.pdf',
    hasImage: false,
  },
  {
    title: 'A Melhor Política e a Caridade Política em Fratelli Tutti',
    subtitle: 'Versaun Tetum',
    pdf: fratelliTuttiPdf,
    downloadName: 'Fratelli-Tutti.pdf',
    hasImage: false,
  },
  {
    title: 'Politika Faramaseutika iha Timor-Leste',
    subtitle: 'Deafiu & Perspetiva',
    pdf: politikaFaramaseutikaPdf,
    downloadName: 'Politika-Faramaseutika.pdf',
    hasImage: false,
  },
  {
    title: 'Legado de Fidel',
    subtitle: 'Cooperação com TL',
    pdf: legadoFidelPdf,
    downloadName: 'Legado-Fidel.pdf',
    hasImage: false,
  },
  {
    title: 'O sistema de Saude em TL na era digital',
    pdf: saudeDigitalPdf,
    downloadName: 'Saude-Digital.pdf',
    hasImage: false,
  },
  {
    title: 'Guarda-na\'in ba Fé iha Foho Sagradu',
    pdf: guardaNainPdf,
    downloadName: 'Guarda-nain-Foho-Sagradu.pdf',
    hasImage: false,
  },
];

function BrochureCard({
  brochure,
  inView,
  delay,
}: {
  brochure: (typeof brochures)[number];
  inView: boolean;
  delay: number;
}) {
  const handleView = () => {
    window.open(brochure.pdf, '_blank');
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = brochure.pdf;
    link.download = brochure.downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 flex flex-col hover:shadow-xl transition-shadow duration-300"
    >
      {brochure.hasImage ? (
        <img
          src={brochure.image}
          alt={brochure.title}
          className="w-full h-56 object-cover"
        />
      ) : (
        <div className="w-full h-56 bg-gradient-to-br from-sky-50 to-sky-100 flex items-center justify-center">
          <FileText size={64} className="text-sky-400" />
        </div>
      )}

      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-xl font-heading font-bold text-campaign-black mb-1">
          {brochure.title}
        </h3>
        {'subtitle' in brochure && brochure.subtitle && (
          <p className="text-sm text-gray-500 mb-4">{brochure.subtitle}</p>
        )}

        <div className="flex flex-wrap gap-3 mt-auto pt-4">
          <button
            onClick={handleView}
            className="inline-flex items-center gap-2 bg-sky-600 text-white px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-sky-700 transition-all duration-300 shadow hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-sky-300"
          >
            <Eye size={16} />
            View
          </button>

          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-2 bg-campaign-black text-white px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-gray-800 transition-all duration-300 shadow hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-gray-400"
          >
            <Download size={16} />
            Download
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export function Brochure() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="brochure" className="py-24 bg-gradient-to-br from-sky-50 to-white relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute -top-30 -left-20 w-96 h-96 bg-sky-200/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-30 -right-20 w-96 h-96 bg-sky-300/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-campaign-black mb-4 relative inline-block">
            Our Brochures
            <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-20 h-1 bg-sky-600" />
          </h2>
          <p className="mt-8 text-lg text-gray-600 max-w-2xl mx-auto">
            Discover more about us. View or download our brochures and documents for detailed information.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {brochures.map((brochure, i) => (
            <BrochureCard key={brochure.downloadName} brochure={brochure} inView={inView} delay={0.2 + i * 0.15} />
          ))}
        </div>
      </div>
    </section>
  );
}
